// hooks/useAbortController.js
import { useCallback, useRef, useState, useEffect } from "react";
import logger from "../utils/UnifiedLogger";

export const useAbortController = () => {
  const [isAborting, setIsAborting] = useState(false);
  const [activeOperations, setActiveOperations] = useState(new Set());

  // Store all abort controllers by operation type
  const abortControllers = useRef({
    api: new Map(),
    audio: new AbortController(),
    socket: new AbortController(),
    global: new AbortController(),
  });

  // Track operation states
  const operationStates = useRef({
    api: new Map(),
    audio: { isPlaying: false, isLoading: false },
    socket: { isRecording: false, isConnected: false },
  });

  // Cleanup function
  const cleanup = useCallback(() => {
    // Clear all API controllers
    abortControllers.current.api.forEach((controller) => {
      if (!controller.signal.aborted) {
        controller.abort("Operation cancelled");
      }
    });
    abortControllers.current.api.clear();

    // Abort audio and socket if not already aborted
    if (!abortControllers.current.audio.signal.aborted) {
      abortControllers.current.audio.abort("Audio operations cancelled");
    }
    if (!abortControllers.current.socket.signal.aborted) {
      abortControllers.current.socket.abort("Socket operations cancelled");
    }

    // Create new controllers
    abortControllers.current.audio = new AbortController();
    abortControllers.current.socket = new AbortController();

    // Clear operation states
    operationStates.current.api.clear();
    operationStates.current.audio = { isPlaying: false, isLoading: false };
    operationStates.current.socket = { isRecording: false, isConnected: false };

    setActiveOperations(new Set());
    setIsAborting(false);

    logger.info("ABORT_CONTROLLER", "All operations cleaned up");
  }, []);

  // Global abort - cancels everything
  const abortAll = useCallback(async () => {
    if (isAborting) return;

    setIsAborting(true);
    logger.warning("ABORT_CONTROLLER", "Aborting all operations");

    try {
      // Abort global controller
      if (!abortControllers.current.global.signal.aborted) {
        abortControllers.current.global.abort("Global abort requested");
      }

      // Cleanup all operations
      cleanup();

      // Create new global controller
      abortControllers.current.global = new AbortController();

      logger.info("ABORT_CONTROLLER", "All operations aborted successfully");
    } catch (error) {
      logger.error("ABORT_CONTROLLER", "Error during global abort", {
        error: error.message,
      });
    }
  }, [isAborting, cleanup]);

  // API-specific abort methods
  const createApiController = useCallback((requestId) => {
    const controller = new AbortController();
    abortControllers.current.api.set(requestId, controller);

    setActiveOperations((prev) => new Set([...prev, `api:${requestId}`]));
    operationStates.current.api.set(requestId, {
      status: "active",
      startTime: Date.now(),
    });

    logger.info("ABORT_CONTROLLER", "API controller created", { requestId });
    return controller;
  }, []);

  const abortApiRequest = useCallback((requestId) => {
    const controller = abortControllers.current.api.get(requestId);
    if (controller && !controller.signal.aborted) {
      controller.abort(`API request ${requestId} cancelled`);
      abortControllers.current.api.delete(requestId);
      operationStates.current.api.delete(requestId);

      setActiveOperations((prev) => {
        const newSet = new Set(prev);
        newSet.delete(`api:${requestId}`);
        return newSet;
      });

      logger.info("ABORT_CONTROLLER", "API request aborted", { requestId });
      return true;
    }
    return false;
  }, []);

  const abortAllApiRequests = useCallback(() => {
    const requestIds = Array.from(abortControllers.current.api.keys());
    let abortedCount = 0;

    requestIds.forEach((requestId) => {
      if (abortApiRequest(requestId)) {
        abortedCount++;
      }
    });

    logger.info("ABORT_CONTROLLER", "All API requests aborted", {
      count: abortedCount,
    });
    return abortedCount;
  }, [abortApiRequest]);

  // Audio-specific abort methods
  const getAudioController = useCallback(() => {
    return abortControllers.current.audio;
  }, []);

  const abortAudio = useCallback((reason = "Audio operation cancelled") => {
    if (!abortControllers.current.audio.signal.aborted) {
      abortControllers.current.audio.abort(reason);

      // Create new controller for future audio operations
      abortControllers.current.audio = new AbortController();
      operationStates.current.audio = { isPlaying: false, isLoading: false };

      setActiveOperations((prev) => {
        const newSet = new Set(prev);
        newSet.delete("audio:player");
        return newSet;
      });

      logger.info("ABORT_CONTROLLER", "Audio operations aborted", { reason });
      return true;
    }
    return false;
  }, []);

  const updateAudioState = useCallback((state) => {
    operationStates.current.audio = {
      ...operationStates.current.audio,
      ...state,
    };

    if (state.isPlaying || state.isLoading) {
      setActiveOperations((prev) => new Set([...prev, "audio:player"]));
    } else if (!state.isPlaying && !state.isLoading) {
      setActiveOperations((prev) => {
        const newSet = new Set(prev);
        newSet.delete("audio:player");
        return newSet;
      });
    }
  }, []);

  // Socket/Recorder-specific abort methods
  const getSocketController = useCallback(() => {
    return abortControllers.current.socket;
  }, []);

  const abortSocket = useCallback((reason = "Socket operation cancelled") => {
    if (!abortControllers.current.socket.signal.aborted) {
      abortControllers.current.socket.abort(reason);

      // Create new controller for future socket operations
      abortControllers.current.socket = new AbortController();
      operationStates.current.socket = {
        isRecording: false,
        isConnected: false,
      };

      setActiveOperations((prev) => {
        const newSet = new Set(prev);
        newSet.delete("socket:recorder");
        newSet.delete("socket:connection");
        return newSet;
      });

      logger.info("ABORT_CONTROLLER", "Socket operations aborted", { reason });
      return true;
    }
    return false;
  }, []);

  const updateSocketState = useCallback((state) => {
    operationStates.current.socket = {
      ...operationStates.current.socket,
      ...state,
    };

    if (state.isRecording) {
      setActiveOperations((prev) => new Set([...prev, "socket:recorder"]));
    } else {
      setActiveOperations((prev) => {
        const newSet = new Set(prev);
        newSet.delete("socket:recorder");
        return newSet;
      });
    }

    if (state.isConnected) {
      setActiveOperations((prev) => new Set([...prev, "socket:connection"]));
    } else {
      setActiveOperations((prev) => {
        const newSet = new Set(prev);
        newSet.delete("socket:connection");
        return newSet;
      });
    }
  }, []);

  // Utility methods
  const getActiveApiRequests = useCallback(() => {
    return Array.from(abortControllers.current.api.keys());
  }, []);

  const getOperationStates = useCallback(() => {
    return {
      api: Object.fromEntries(operationStates.current.api),
      audio: operationStates.current.audio,
      socket: operationStates.current.socket,
      activeOperations: Array.from(activeOperations),
    };
  }, [activeOperations]);

  const isGlobalAborted = useCallback(() => {
    return abortControllers.current.global.signal.aborted;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  return {
    // Global controls
    abortAll,
    isAborting,
    isGlobalAborted,
    activeOperations,

    // API controls
    createApiController,
    abortApiRequest,
    abortAllApiRequests,
    getActiveApiRequests,

    // Audio controls
    getAudioController,
    abortAudio,
    updateAudioState,

    // Socket controls
    getSocketController,
    abortSocket,
    updateSocketState,

    // Utility
    getOperationStates,
    cleanup,
  };
};
